<template>
  <div class="card">
    <h6>ADDRESS DETAILS</h6>

    <div class="form-group">
      <div class="col-sm-8">
        <div class="home-address">
          <p class="_margin-top-0 _margin-bottom-0">
            {{
              address.present
                | objectToCsv(["line1", "line2", "county", "city", "postcode"])
            }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="form-group _margin-top-20 _margin-bottom-20"
      v-if="!address.present.mprn && showMprnInput"
    >
      <div class="col-sm-7">
        <uiv-alert type="info" v-if="!terms.mprnNumber">
          Looks like we were unable to find your gas MPRN meter number from the
          National Meter Database. Please enter it or click 'Don't know your
          MPRN' to select your address
        </uiv-alert>

        <div class="row">
          <div class="col-xs-7">
            <form-label id="mprn"
              >Please enter your your gas MPRN meter number</form-label
            >
          </div>

          <div class="col-xs-5 text-right">
            <uiv-btn
              type="link"
              size="sm"
              style="padding: 0; margin-bottom: 5px"
              @click="onGetExoServeAddress"
              v-if="!showMprnLookup"
              >Don't know your MPRN?</uiv-btn
            >
            <uiv-btn
              type="link"
              size="sm"
              style="padding: 0; margin-bottom: 5px"
              @click="showMprnLookup = false"
              v-else
              >Enter MPRN manually</uiv-btn
            >
          </div>
        </div>

        <validation-messages
          label="Meter Point Reference Number"
          :$v="$form.terms.mprnNumber"
          v-show="!showMprnLookup"
        >
          <form-field
            id="mprn"
            v-model="terms.mprnNumber"
            type="number"
            :error="$form.terms.mprnNumber.$error"
            maxlength="10"
            @change="$form.terms.mprnNumber.$touch()"
          />
        </validation-messages>

        <uiv-popover
          title="Meter Point Reference Number"
          target="#mprn"
          trigger="hover"
        >
          <template slot="popover">
            The MPRN (M number, Meter Point Reference Number) is unique to your
            property. It should be printed on your gas bill. Do not confuse it
            with your gas supplier account number or your meter serial number.
            The format of a MPRN is quite standard and consists of between 6 and
            10 numerical digits only. If you cannot find your MPRN on your
            recent bill you can find it out by calling Tranco's MPRN helpline on
            {{ appSettings.mprnHelpline }}
          </template>
        </uiv-popover>

        <loader :show="requesting.mprn" />

        <div class="row" v-show="showMprnLookup && !requesting.mprn">
          <div class="col-xs-12">
            <select-list
              ref="mprnLookup"
              id="mprnLookup"
              v-model="selectedMprn"
              :list="mprnAddressList"
              placeholder="Select an address"
              value-key="text"
              label-key="text"
              @change="onChooseMprnAddress"
            />
          </div>

          <div class="col-xs-12">
            <uiv-alert type="info" v-if="noMprnFound" class="_margin-top-10">
              Unable to locate MPRN for this address.
            </uiv-alert>
          </div>
        </div>
      </div>
    </div>

    <div
      class="form-group _margin-bottom-20"
      v-if="!address.present.mpan && showMpanInput"
    >
      <div class="col-sm-7">
        <uiv-alert type="info" v-if="!terms.mpanNumber">
          Looks like we were unable to find your electricity MPAN meter number
          from the National Meter Database. Please enter your meter number
        </uiv-alert>

        <form-label id="mpan"
          >Please enter your electricity MPAN meter number</form-label
        >

        <validation-messages
          label="Meter Point Administration Number"
          :$v="$form.terms.mpanNumber"
        >
          <form-field
            id="mpan"
            type="nospace"
            v-model="terms.mpanNumber"
            maxlength="22"
            @change="onChangeMpan"
            :error="$form.terms.mpanNumber.$error"
          />

          <template v-slot:error>
            <div v-if="!$form.terms.mpanNumber.domesticMeter">
              Must be a domestic meter
            </div>
            <div v-if="!$form.terms.mpanNumber.checked">MPAN is invalid</div>
          </template>
        </validation-messages>

        <uiv-popover
          title="Meter Point Administration Number"
          target="#mpan"
          trigger="hover"
        >
          <template slot="popover">
            The MPAN (Meter Point Administration Number) is unique to your
            property. It is found on the electricity bill issued by your
            supplier. This is sometimes called a Supply Number but it should not
            be confused with your customer reference number or your meter serial
            number. The full MPAN is 21-22 digits in length. If you cannot find
            your MPAN on your recent bill, please read our
            <a
              href="http://www.myutilitygenius.co.uk/energy-guide/mpan/"
              target="_blank"
              >guide section</a
            >
            on our website. If you are still struggling to find the MPAN on your
            bill you can also call your electricity supplier.
          </template>
        </uiv-popover>
      </div>
    </div>

    <uiv-alert
      type="danger"
      v-if="terms.mpanNumber && !$form.terms.mpanNumber.economy7Meter"
    >
      <b>Please verify your selection</b> <br />
      You have selected a non economy 7 metered address line. Please check your
      selection.
    </uiv-alert>

    <uiv-alert
      type="danger"
      v-if="terms.mpanNumber && !$form.terms.mpanNumber.domesticMeter"
    >
      <b>Non Domestic Meter</b> <br />
      You have selected a non-domestic meter. We are unable to provide a switch
      for these types of meters.
    </uiv-alert>
  </div>
</template>

<script>
import PreviousAddressess from "./PreviousAddressess";
import { mapState, mapActions } from "vuex";

export default {
  name: "CustomerAddress",
  props: {
    $form: Object,
  },
  data() {
    return {
      showMprnLookup: false,
      noMprnFound: false,
      selectedMprn: null,

      billingAddressList: [],
      mprnAddressList: [],
      requesting: {
        billing: false,
        mprn: false,
      },
    };
  },
  computed: {
    ...mapState({
      appSettings: ({ app_settings }) => app_settings,
      switchPlan: ({ personal_details_switch_plan }) =>
        personal_details_switch_plan,
      quote: ({ storage }) => storage.quote,
      showMprnInput: ({ storage }) =>
        storage.quote.switchType != "electricityToElectricity",
      showMpanInput: ({ storage }) => storage.quote.switchType != "gasToGas",
      address: ({ personal_details_address }) =>
        personal_details_address.residentialAddresses,
      businessAddress: ({ personal_details_address }) =>
        personal_details_address.businessAddress,
      terms: ({ personal_details_terms }) => personal_details_terms,
      residentialStatusList: ({ lookups }) => lookups.residentialStatuses,
    }),
  },
  methods: {
    ...mapActions({
      findAddresses: "lookups/getAddresses",
      getXoserveMeters: "lookups/getXoserveMeters",
      resetBillingAddress: "personal_details_address/resetBillingAddress",
      validMpan: "personal_details_address/validMpan",
    }),
    onReset() {
      this.resetBillingAddress();
      this.$refs.billLookup.onReset();
    },
    async onGetBillingAddress(postcode) {
      this.requesting.billing = true;
      this.billingAddressList = [];
      this.$form.address.businessAddress.postcode.$touch();
      if (this.$form.address.businessAddress.postcode.$invalid) return;

      this.billingAddressList = await this.findAddresses(postcode);
      this.requesting.billing = false;
    },
    async onGetExoServeAddress() {
      this.requesting.mprn = true;
      this.mprnAddressList = [];
      this.terms.mprnNumber = null;
      this.selectedMprn = null;
      this.noMprnFound = false;

      this.mprnAddressList = await this.findAddresses(
        this.address.present.postcode
      );

      this.requesting.mprn = false;
      this.showMprnLookup = true;
    },
    async onChooseMprnAddress(address) {
      if (!address) return;
      const selected = this.mprnAddressList.find(({ text }) => text == address);
      const xoserveMeters = await this.getXoserveMeters(selected);
      const gasdetails =
        xoserveMeters.gasdetails.find(({ isDomestic }) => isDomestic) || {};
      const { mprn, isPrepayment } = gasdetails;

      this.noMprnFound =
        !mprn ||
        (isPrepayment == true &&
          this.switchPlan.payMethod != "PrePaymentMeter");

      if (this.noMprnFound == false) {
        this.selectedMprnAddress = selected.text;
        this.terms.mprnNumber = mprn;
        this.showMprnLookup = false;
      }
    },
    onChooseBillingAddress(selected) {
      this.businessAddress.line1 = selected.addressLine1;
      this.businessAddress.line2 = selected.addressLine2;
      this.businessAddress.city = selected.city;
      this.businessAddress.county = selected.county;
      this.businessAddress.postcode = selected.postcode.replace(" ", "");
      this.businessAddress.text = selected.text;
      this.$forceUpdate();
    },
    onTimeAtAddress() {
      const years = (y) => parseInt(y || 0);
      const months = (m) => parseInt(m || 0);
      const total =
        years(this.address.years) * 12 + months(this.address.months);

      this.address.years = this.address.years == 0 ? null : this.address.years;
      this.address.months =
        this.address.months == 0 ? null : this.address.months;
      this.address.present.lengthOfResidencyInMonths = total;
    },
    onCantFindAddress() {
      this.resetBillingAddress();
      this.$refs.billLookup.onReset();
      this.businessAddress.manuallyEnterBilling =
        !this.businessAddress.manuallyEnterBilling;
      this.$form.address.businessAddress.postcode.$reset();
    },
    async onChangeMpan(value) {
      this.$form.terms.mpanNumber.$touch();
      this.terms.mpanValid = null;

      if (
        this.$form.terms.mpanNumber.domesticMeter &&
        this.$form.terms.mpanNumber.economy7Meter
      ) {
        this.terms.mpanValid = await this.validMpan(value);
        this.$form.terms.mpanValid.$touch();
      }
    },
  },
  components: {
    PreviousAddressess,
  },
};
</script>

<style lang="scss" scoped>
.home-address {
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #dadada;
}
</style>
